import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Icon } from '@/hen-ui/atomics/Icon';
import type { TimelineEventDescriptionProps } from './TimelineEventDescription';

type TimelineEventDescriptionElement =
  React.ReactElement<TimelineEventDescriptionProps>;

/**
 * Design-token color options for the circle marker. Each maps to a `text-*`
 * token class applied to the marker Icon. `default` preserves the original
 * marker color (`text-primary-700`) so existing consumers see no change.
 */
const MARKER_COLOR_CLASSES = {
  default: 'text-primary-700',
  primary: 'text-primary-500',
  secondary: 'text-secondary-500',
  accent: 'text-accent-500',
  neutral: 'text-neutral-500',
  success: 'text-success-500',
  warning: 'text-warning-500',
  error: 'text-error-500',
} as const;

export type TimelineMarkerColor = keyof typeof MARKER_COLOR_CLASSES;

/** Runtime list of valid marker color tokens, for validating untrusted input. */
export const TIMELINE_MARKER_COLORS = Object.freeze(
  Object.keys(MARKER_COLOR_CLASSES) as TimelineMarkerColor[],
) as readonly TimelineMarkerColor[];

/** Type guard for narrowing an arbitrary string to a valid marker color. */
export const isTimelineMarkerColor = (
  value: unknown,
): value is TimelineMarkerColor =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(MARKER_COLOR_CLASSES, value);

export interface TimelineEventProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  index: number;
  /** When true the circle marker beside the title is not rendered. */
  hideMarker?: boolean;
  /** Design-token color applied to the circle marker. */
  color?: TimelineMarkerColor;
  /** TimelineEventDescription child content for the event body. */
  children?:
    | TimelineEventDescriptionElement
    | TimelineEventDescriptionElement[];
}

const ICON_SIZE = '30px';

/**
 * TimelineEvent component renders a single event in the timeline.
 * Composed of Heading for the title and TimelineEventDescription for the body content.
 */
export const TimelineEvent = React.forwardRef<
  HTMLDivElement,
  TimelineEventProps
>(
  (
    {
      title,
      index,
      hideMarker,
      color = 'default',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <div ref={ref} className={cn('relative', className)} {...props}>
        <div className='flex flex-col gap-xs'>
          {/* Heading row with icon */}
          <div className='flex gap-xxs items-center'>
            {!hideMarker && (
              <div className='flex-shrink-0 relative flex items-center justify-center'>
                <Icon
                  icon='circle'
                  size='lg'
                  className={cn(
                    MARKER_COLOR_CLASSES[color] ?? MARKER_COLOR_CLASSES.default,
                  )}
                  style={{ width: ICON_SIZE, height: ICON_SIZE }}
                  aria-hidden='true'
                />
              </div>
            )}

            {/* Right column: Title */}
            <div className='flex-1'>
              <Heading variant='h4'>{title}</Heading>
            </div>
          </div>

          {/* Description row expects TimelineEventDescription */}
          <div>{children}</div>
        </div>
      </div>
    );
  },
);

TimelineEvent.displayName = 'TimelineEvent';
